import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { VehicleType } from '../../../vehicle/params/vehicle-type';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { MobilizationSalaryRule } from '../../models/mobilization.model';
import { MobilizationService } from '../../mobilization.service';

@Component({
    selector: 'app-salary-rule-list',
    standalone: true,
    templateUrl: './salary-rule-list.component.html',
    styleUrls: ['../salary-rule-page.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class SalaryRuleListComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    rows: MobilizationSalaryRule[] = [];
    vehicleTypes: VehicleType[] = [];
    isLoading = false;
    totalRecords = 0;
    first = 0;
    rowsPerPage = 10;
    keyword = '';
    active: boolean | null = null;
    vehicleTypeId?: number;
    readonly activeOptions = [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];
    private updating = new Set<number>();

    constructor(
        private mobilizationService: MobilizationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Mobilization', routerLink: '/fleet/mobilization' },
            { label: 'Salary Rules' }
        ];
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
    }

    loadRules(event?: TableLazyLoadEvent): void {
        if (event) {
            this.first = event.first ?? 0;
            this.rowsPerPage = event.rows ?? 10;
        }

        const companyId = this.companyId();
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        this.mobilizationService.searchSalaryRules({
            owningCompanyId: companyId,
            page: Math.floor(this.first / this.rowsPerPage),
            pageSize: this.rowsPerPage,
            active: this.active,
            vehicleTypeId: this.vehicleTypeId,
            keyword: this.keyword
        }).subscribe({
            next: response => {
                this.rows = response.items ?? [];
                this.totalRecords = response.count ?? this.rows.length;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load salary rules.' });
            }
        });
    }

    search(): void {
        this.first = 0;
        this.loadRules();
    }

    clearSearch(): void {
        this.keyword = '';
        this.active = null;
        this.vehicleTypeId = undefined;
        this.search();
    }

    create(): void {
        this.router.navigate(['/fleet/mobilization/salary-rules/new']);
    }

    view(row: MobilizationSalaryRule): void {
        if (row.salaryRuleId) {
            this.router.navigate(['/fleet/mobilization/salary-rules', row.salaryRuleId]);
        }
    }

    edit(row: MobilizationSalaryRule): void {
        if (row.salaryRuleId) {
            this.router.navigate(['/fleet/mobilization/salary-rules/edit', row.salaryRuleId]);
        }
    }

    updateActive(row: MobilizationSalaryRule, active: boolean): void {
        if (!row.salaryRuleId || row.active === active) {
            return;
        }

        const previous = row.active !== false;
        row.active = active;
        this.updating.add(row.salaryRuleId);
        this.mobilizationService.updateSalaryRuleActive(row.salaryRuleId, active).subscribe({
            next: response => {
                row.active = response.data?.active ?? active;
                this.updating.delete(row.salaryRuleId!);
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Salary rule marked ${active ? 'active' : 'inactive'}.` });
            },
            error: () => {
                row.active = previous;
                this.updating.delete(row.salaryRuleId!);
                this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Could not update salary rule.' });
            }
        });
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Rules', value: this.totalRecords, hint: 'Matching salary rules', tone: 'info' },
            { label: 'Loaded', value: this.rows.length, hint: 'Rows in current page', tone: 'default' },
            { label: 'Active', value: this.rows.filter(row => row.active !== false).length, hint: 'Visible active rules', tone: 'success' },
            { label: 'Currencies', value: new Set(this.rows.map(row => row.currencyCode || 'SAR')).size, hint: 'Visible currency codes', tone: 'warn' }
        ];
    }

    activeFilterCount(): number {
        return [this.keyword, this.vehicleTypeId, this.active === null ? '' : 'active'].filter(Boolean).length;
    }

    activeSeverity(row: MobilizationSalaryRule): 'success' | 'danger' {
        return row.active === false ? 'danger' : 'success';
    }

    isUpdating(row: MobilizationSalaryRule): boolean {
        return !!row.salaryRuleId && this.updating.has(row.salaryRuleId);
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    amountLabel(row: MobilizationSalaryRule): string {
        return `${row.dailyRate ?? 0} ${row.currencyCode || 'SAR'}`;
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
